import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { type LoginCompanyChoice, isSelectCompanyError, useAuth } from "@/features/auth/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { Building2, Eye, EyeOff, Globe, Lock, Smartphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorPayload } from "@/lib/api";
import { isValidSaudiMobile } from "@/lib/saudi-phone";
import { getPostLoginRedirect } from "@/lib/user-permissions";
import { Loading } from "@/components/ui/loader";

type LoginFormData = {
	phone: string;
	password: string;
	rememberMe?: boolean;
};

export const LoginPage: React.FC = () => {
	const { t } = useTranslation();
	const { language, setLanguage } = useLanguage();
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { user, isAuthenticated, isLoading: isAuthLoading, login } = useAuth();
	const { toast } = useToast();
	const navigate = useNavigate();
	const location = useLocation();

	const from = location.state?.from?.pathname || "/";
	// Filled when the mobile and password match accounts in several companies.
	const [companies, setCompanies] = useState<LoginCompanyChoice[] | null>(null);
	const [companyId, setCompanyId] = useState<number | null>(null);

	const loginSchema = z.object({
		phone: z
			.string()
			.trim()
			.min(1, t("auth.validation.phoneRequired"))
			.refine(isValidSaudiMobile, t("auth.validation.phoneInvalid")),
		password: z.string().min(3, t("common.validation.passwordMinLength")),
		rememberMe: z.boolean().optional(),
	});

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
		// Checked by default: unchecking keeps the session only until the browser is closed.
		defaultValues: { rememberMe: true },
	});

	// A different mobile or password may match a different set of companies.
	const phoneValue = watch("phone");
	const passwordValue = watch("password");
	useEffect(() => {
		setCompanies(null);
		setCompanyId(null);
	}, [phoneValue, passwordValue]);

	if (isAuthLoading) return <Loading />;
	if (isAuthenticated && user) return <Navigate to={getPostLoginRedirect(from, user)} replace />;

	const onSubmit = async (data: LoginFormData) => {
		setIsLoading(true);
		try {
			const user = await login(data.phone, data.password, data.rememberMe ?? true, companyId ?? undefined);
			navigate(getPostLoginRedirect(from, user), { replace: true });
		} catch (error) {
			if (isSelectCompanyError(error)) {
				setCompanies(error.companies);
				setCompanyId(null);
				return;
			}
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const toggleLanguage = () => {
		setLanguage(language === "en" ? "ar" : "en");
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-card">
			<div className="w-full flex items-center justify-center flex-col gap-8">
				<div className="flex items-center justify-center flex-col gap-4 w-full">
					<img src="/images/logo.png" alt="logo" className="w-24 h-24 mx-auto" />
					<p className="text-xl font-bold text-primary">{t("auth.pos")}</p>
				</div>
				<div className="w-full items-center max-w-md justify-center px-4">
					<Card className="shadow-card w-full border border-input p-6 pt-2 flex flex-col rounded-xl ">
						<CardHeader className="text-center space-y-2 flex flex-col items-start px-0">
							<CardTitle className="text-base font-bold text-foreground">{t("auth.welcomeBack")}</CardTitle>
							<CardDescription className="text-foreground">{t("auth.signInDescription")}</CardDescription>
						</CardHeader>
						<CardContent className="p-0">
							<form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
								<div className="space-y-2">
									<label htmlFor="phone" className="text-sm font-medium text-foreground">
										{t("auth.phone")}
									</label>
									<div className="relative">
										<Smartphone className="absolute right-3 ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 text-primary w-4 h-4" />
										<Input
											id="phone"
											type="tel"
											inputMode="tel"
											autoComplete="username"
											placeholder={t("auth.phonePlaceholder")}
											className="pl-10 pr-10 h-12 border border-input bg-card placeholder:text-muted-foreground text-primary rtl:text-end ltr:text-start"
											dir="ltr"
											aria-invalid={!!errors.phone}
											{...register("phone")}
										/>
									</div>
									{errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
								</div>

								<div className="space-y-2">
									<label htmlFor="password" className="text-sm font-medium text-foreground">
										{t("auth.password")}
									</label>
									<div className="relative">
										<Lock className="absolute right-3 ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 text-primary w-4 h-4" />
										<Input
											id="password"
											type={showPassword ? "text" : "password"}
											placeholder={t("auth.passwordPlaceholder")}
											autoComplete="current-password"
											className="px-10 h-12 border border-input bg-card placeholder:text-muted-foreground text-primary"
											{...register("password")}
										/>
										<button
											type="button"
											onClick={() => setShowPassword(!showPassword)}
											aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
											aria-pressed={showPassword}
											className="absolute ltr:right-3 rtl:left-3 top-1/2 transform -translate-y-1/2 text-foreground hover:text-foreground"
										>
											{showPassword ? (
												<EyeOff className="w-4 h-4" />
											) : (
												<Eye className="w-4 h-4" />
											)}
										</button>
									</div>
									{errors.password && (
										<p className="text-sm text-destructive">{errors.password.message}</p>
									)}
								</div>

								{companies && (
									<fieldset className="space-y-2">
										<legend className="text-sm font-medium text-foreground">
											{t("auth.errors.selectCompany")}
										</legend>
										{companies.map(company => (
											<label
												key={company.id}
												className="flex h-12 cursor-pointer items-center gap-3 rounded-md bg-card px-3 text-primary"
											>
												<input
													type="radio"
													name="companyId"
													value={company.id}
													checked={companyId === company.id}
													onChange={() => setCompanyId(company.id)}
												/>
												<Building2 className="w-4 h-4 shrink-0" />
												<span className="truncate text-sm">
													{company.name ?? `#${company.id}`}
												</span>
											</label>
										))}
									</fieldset>
								)}

								<div className="flex items-center justify-between">
									<label className="flex items-center space-x-2">
										<input
											type="checkbox"
											className="rounded border-white text-primary focus:ring-primary me-2"
											{...register("rememberMe")}
										/>
										<span className="text-sm text-foreground">{t("auth.rememberMe")}</span>
									</label>
									<Link
										to="/forgot-password"
										className="text-sm text-foreground hover:text-foreground/80 hover:underline"
									>
										{t("auth.forgotPassword")}
									</Link>
								</div>
								<br />
								<Button
									type="submit"
									className="w-full mt-4 h-12 font-bold"
									disabled={isLoading || (companies != null && companyId == null)}
								>
									{isLoading ? t("auth.signingIn") : t("auth.signIn")}
								</Button>
							</form>
						</CardContent>

						<Button
							variant="ghost"
							size="sm"
							onClick={toggleLanguage}
							className="w-full gap-2 hover:bg-transparent mt-4"
						>
							<Globe className="w-5 h-5 text-muted-foreground" />
							<span className="text-sm text-muted-foreground">
								{language === "en" ? "العربية" : "English"}
							</span>
						</Button>
					</Card>
				</div>
			</div>
		</div>
	);
};
